import { redirect } from 'react-router-dom';

// Constants
import { formNaming } from '../../../../../../components/forms/NewCommunicationForm/constants';

// GraphQL
import { client } from '../../../../../../gql/client';
import { SAVE_COMMUNICATION } from '../../../../../../gql/mutations/communication';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../../../../gql/queries/orders/predefinedData';
import {
  prependCampaignToCache,
  prependCommunicationToCache,
  prependGenericOrderToCache,
} from '../../../../../../gql/cacheHelpers';
import { SAVE_CAMPAIGN } from '../../../../../../gql/mutations/campaign';
import { SAVE_GENERIC_ORDER } from '../../../../../../gql/mutations/generic-order';
import { toTeamIds } from '../../../../../../utils/forms';

// State
import { indexTabFormReset } from '../../../../../../state/actions/ui/tabs';
import { deselectedRowModalTable } from '../../../../../../state/actions/ui/selectRowTableModal';

// Offline compound queue
import { addCompoundOperation } from '../../../../../../offline/offlineCompoundQueue';
import { isOnlineNow } from '../../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../../offline/checkConnectivity';

// Types
import { OrderState, OrderType } from '../../../../../../types/orders';
import { SaveDataParams } from './types';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

//Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import { tabsType } from '../../../../../../state/reducers/ui/tabs';
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';

import { getCursor } from '../../../../../../state/selectors/ui/tables';
import { assertDraftOwner } from '../../../../../../utils/draftOwnership';
import {
  COMMUNICATION_CREATION_SCOPE,
  communicationCreationOwnerId,
} from '../../../../../../utils/draftScopes';
import {
  releaseCommunicationCreationDraft,
  requireCommunicationCreationParent,
  trustedCommunicationSelection,
} from '../../../../../../utils/communicationCreationDraft';
import { DraftOwnerMismatchError } from '../../../../../../utils/draftOwnership';
import { getModalKeyByTab } from '../../../../../../utils/getModalKeyByTab';
import * as Sentry from '@sentry/react';

export const saveData = async ({
  dataFormRaw,
  tabSelected,
  storedDataOTSelected: rawSelection,
  store,
  params,
}: SaveDataParams) => {
  const dispatch = store.dispatch;
  if (dataFormRaw) {
    const cursorDuplicateCommunication = getCursor(
      tableIds.DUPLICATE_COMMUNICATION
    )(store.getState());
    const cursorSearchAndCreateCommunication = getCursor(
      tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS
    )(store.getState());
    const cursorWorkOrdersCommunication = getCursor(
      tableIds.WORKORDER_COMMUNICATIONS
    )(store.getState());
    const cursorCampaign = getCursor(tableIds.CAMPAIGNS)(store.getState());
    const cursorCampaignModal = getCursor(tableIds.CAMPAIGN_MODAL)(
      store.getState()
    );
    const cursorGenericOrder = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(
      store.getState()
    );
    const cursorGenericOrderDuplicate = getCursor(
      tableIds.DUPLICATE_GENERIC_ORDER
    )(store.getState());
    const cursorGenericOrderSearchAndCreate = getCursor(
      tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS
    )(store.getState());
    const cursorGenericOrderModal = getCursor(tableIds.GENERIC_ORDER_MODAL)(
      store.getState()
    );

    const creationDate = new Date().toISOString();

    let orderCommunicationInput, dataOrderParent;
    const dataForm = JSON.parse(decompressSessionStorage(dataFormRaw));

    // 🔴 Barrera (K3 de la batería QA del navegador, 2026-11-03; spec
    // 2026-11-02 wizard-draft-ownership): en los árboles de OT y de campaña la
    // comunicación es del PADRE de la URL (`:id`), nunca del de un borrador de
    // otra OT. Antes el padre heredado salía de `dataForm.parentOrderId`: con
    // la comunicación de la OT 801 a medias, la URL de la 800 la creaba BAJO
    // 801. Dos comprobaciones, las dos antes de la rama sin red (tampoco se
    // encola un alta ajena) y de cualquier escritura:
    //  1. El borrador es de este dueño (`parent:<:id>`, o `standalone` en la
    //     sección suelta, que así rechaza un borrador de OT).
    //  2. En los árboles de OT/campaña, falla CERRADO: sin dueño o con la
    //     copia de otro padre tampoco se guarda.
    // No debería saltar nunca —el loader descarta el borrador ajeno y vuelve a
    // la ficha del padre—; si salta no se escribe NI se encola nada y la action
    // enseña el toast genérico. Aquí no hay reintento de cola: la cola
    // reproduce `syncCommunication` con el `commInput` ya capturado.
    const urlParentId = params?.id;
    assertDraftOwner(
      COMMUNICATION_CREATION_SCOPE,
      communicationCreationOwnerId(urlParentId),
      { isReplay: false }
    );
    requireCommunicationCreationParent(urlParentId, dataForm);
    // El padre HEREDADO (el que puso la puerta) es el de la URL. En la sección
    // suelta no hay padre heredado: el usuario elige la OT o la campaña en el
    // modal (`storedDataOTSelected`) o crea una OT nueva (pestaña 0). Una OT
    // elegida a mano en el modal, o la pestaña 0, siguen mandando también bajo
    // una OT: es lo que el usuario ve y eligió.
    const inheritedParentId: string | undefined = urlParentId;
    // La OT/campaña del modal solo vale si es la de la URL o la elegida A MANO
    // en ESTA alta (revisión de la Task 12a): redux-persist la guarda en
    // `localStorage`, que comparten las pestañas, y al recargar podía volver la
    // de OTRA pestaña — el dueño y la copia seguían siendo de esta, así que la
    // barrera pasaba y se guardaba bajo la OT de la otra. Si no vale, se ignora
    // (manda el padre de la URL) y se suelta de Redux.
    const modalKey = getModalKeyByTab(formNaming.NEW_COMMUNICATION_FORM, tabSelected);
    const storedDataOTSelected = trustedCommunicationSelection(
      modalKey,
      rawSelection,
      urlParentId
    ) as { id?: string; roid?: string | string[] };
    const ignoredForeignSelection = !!rawSelection?.id && !storedDataOTSelected?.id;
    if (ignoredForeignSelection) dispatch(deselectedRowModalTable(modalKey));
    // En la sección suelta no hay padre de la URL al que volver: sin la OT
    // elegida en ESTA alta no se guarda (falla CERRADO, toast genérico).
    if (tabSelected > 0 && !inheritedParentId && ignoredForeignSelection) {
      const mismatch = new DraftOwnerMismatchError(
        COMMUNICATION_CREATION_SCOPE.name,
        typeof rawSelection?.id === 'string' ? `selección:${rawSelection.id}` : null,
        'selección de esta alta'
      );
      Sentry.captureException(mismatch, {
        tags: { guard: `draft-owner-mismatch:${COMMUNICATION_CREATION_SCOPE.name}` },
      });
      throw mismatch;
    }
    const pkInitArray = dataForm.pkInit
      .replace(/ /g, '')
      .replace(/ /g, '')
      .split('+');
    const pkEndArray = dataForm.pkEnd
      .replace(/ /g, '')
      .replace(/ /g, '')
      .split('+');
    /// Step 1: Generate data Communication
    // Determine if we should use parent order from storage:
    // - On tabs 1 and 2 (relate to OT / campaign)
    // - When there is a parent in the URL (coming from ShowOT)
    // - AND there is no valid selection in the modal for this draft
    // La pestaña 2 entra desde la revisión de la Task 12a: en el árbol de
    // campaña, si se ignora una selección de otra pestaña, el padre es la
    // campaña de la URL (antes la pestaña 2 solo miraba el modal).
    const useParentOrderFromStorage =
      tabSelected > 0 && !!inheritedParentId && !storedDataOTSelected?.id;

    // ── OFFLINE BRANCH (Bloque C-7.3) ──
    // Si el usuario está sin red, encolamos los inputs ya construidos en la
    // compound queue (mismo patrón que action/accident). Para Tab 0 esto evita
    // el bug de encolar solo SAVE_GENERIC_ORDER y perder la SaveCommunication
    // child. Al reconectar, syncCompoundQueue ejecuta parent + child en orden.
    // Sondeo real (spec 2026-09-11): con cobertura mala `navigator.onLine` dice
    // `true`, así que esta rama NO corría y el guardado se iba por el camino
    // con red — donde el padre lleva `skipOfflineQueue`, o sea SIN techo: se
    // colgaba hasta que el sistema se rendía y la comunicación se perdía, que
    // es justo lo que esta rama existe para evitar.
    const hayBackend = isOnlineNow() ? await isActuallyOnline() : false;
    if (!hayBackend) {
      // Si el usuario entró desde una OT existente (parentOrderId en
      // sessionStorage) y no ha cambiado de OT en el modal, tratamos como
      // Tab 1 aunque el Redux state diga Tab 0 (mismo principio que
      // useParentOrderFromStorage del flujo online — soluciona el caso en
      // que el preselect del tab no haya disparado el dispatch a Redux).
      const hasInheritedParent =
        !!inheritedParentId && !storedDataOTSelected?.id;
      const effectiveTab =
        tabSelected === 0 && hasInheritedParent ? 1 : tabSelected;

      const isCampaignParentTab0 =
        effectiveTab === 0 && dataForm?.specialTag === OrderType.Campaign;
      const teamIds = toTeamIds(dataForm.teamRelationships);

      const commInputBase: Record<string, unknown> = {
        sendStatus: dataForm.sendStatus,
        state: OrderState.Open,
        creationDate,
        registerInitDate: dataForm.registerInitDate,
        pkInitKm: Number(pkInitArray[0]),
        pkInitMeter: Number(pkInitArray[1]),
        pkEndKm: Number(pkEndArray[0]),
        pkEndMeter: Number(pkEndArray[1]),
        track: dataForm.track,
        direction: dataForm?.direction,
        margin: dataForm?.margin,
        lane: dataForm.lane,
        concessionId: dataForm.concession,
        roadId: dataForm.road,
        capitolId: dataForm.capitol,
        subCapitolId: dataForm.subCapitol,
        classification: dataForm.classification,
        ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
        description: dataForm.description,
        notes: dataForm.notes,
        interlocutorGroupId: dataForm.groupInter,
        mediaOfCommunicationId: dataForm.media,
      };

      let parentMutation: 'SAVE_GENERIC_ORDER' | 'SAVE_CAMPAIGN' | null = null;
      let parentInput: Record<string, unknown> | null = null;
      let parentOrderIdForComm: string | undefined;
      let parentOrderRoid: string | undefined;
      let redirectPath: string;

      if (effectiveTab === 0) {
        // Crear OT padre + comm en sync
        parentMutation = isCampaignParentTab0
          ? 'SAVE_CAMPAIGN'
          : 'SAVE_GENERIC_ORDER';
        parentInput = {
          state: OrderState.Open,
          creationDate,
          registerInitDate: dataForm.registerInitDate,
          pkInitKm: Number(pkInitArray[0]),
          pkInitMeter: Number(pkInitArray[1]),
          pkEndKm: Number(pkEndArray[0]),
          pkEndMeter: Number(pkEndArray[1]),
          track: dataForm.track,
          direction: dataForm?.direction,
          margin: dataForm?.margin,
          lane: dataForm.lane,
          concessionId: dataForm.concession,
          roadId: dataForm.road,
          teamIds,
          capitolId: dataForm.capitol,
          subCapitolId: dataForm.subCapitol,
          classification: dataForm.classification,
          ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
          description: dataForm.description,
          notes: dataForm.notes,
          ...(isCampaignParentTab0 && {
            name: 'Campanya relacionada amb una comunicacio',
          }),
        };
        // Sin parent real aún; sync resolverá parentOrderId tras crear el padre.
        redirectPath = '/communication';
      } else {
        // Tab 1/2 (real o inferido por hasInheritedParent) — parent existe.
        parentOrderIdForComm = hasInheritedParent
          ? inheritedParentId
          : storedDataOTSelected?.id;
        parentOrderRoid = hasInheritedParent
          ? dataForm.parentOrderRoid
          : storedDataOTSelected?.roid;
        commInputBase.parentOrderId = parentOrderIdForComm;
        redirectPath =
          effectiveTab === 2
            ? `/work-orders/campaign/${parentOrderIdForComm}`
            : `/work-orders/${parentOrderIdForComm}`;
      }

      await addCompoundOperation({
        type: 'communication',
        parentOrderId: parentOrderIdForComm ?? '',
        parentOrderRoid:
          (Array.isArray(parentOrderRoid)
            ? parentOrderRoid.join('.')
            : parentOrderRoid) ?? '',
        isCampaignParent: effectiveTab === 2 || isCampaignParentTab0,
        sessionSnapshots: {},
        localImages: [],
        communicationData: {
          parentMutation,
          parentInput,
          commInput: commInputBase,
          redirectPath,
        },
      });

      // Cleanup (mismo patrón que el flujo online): las copias del alta y su
      // dueño.
      releaseCommunicationCreationDraft();
      sessionStorage.removeItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);

      dispatch(deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM));
      dispatch(deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM_OT));
      dispatch(
        deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN)
      );
      dispatch(indexTabFormReset(tabsType.COMMUNICATION));
      window.dispatchEvent(new CustomEvent('offline-communication-queued'));

      return redirect(redirectPath);
    }

    // Tab 0: ALWAYS create new order (user explicitly chose "Create new OT")
    if (tabSelected === 0) {
      orderCommunicationInput = {
        type: OrderType.Communication,
        state: OrderState.Open,
        creationDate,
        registerInitDate: dataForm.registerInitDate,
        specialTag: dataForm?.specialTag,
        description: dataForm.description,
        notes: dataForm.notes,
        classification: dataForm.classification,
        capitol: dataForm.capitol,
        subCapitol: dataForm.subCapitol,
        ...(dataForm.predefine && { predefinedOrder: dataForm.predefine }),
      };
    }
    // Tab 1 or 2: Link to existing order (from modal selection or parent from storage)
    // Communication keeps its OWN predefinido/capitol/subcapitol from the form
    if (tabSelected > 0) {
      const orderId = useParentOrderFromStorage
        ? inheritedParentId
        : storedDataOTSelected?.id;

      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: orderId,
        },
      });

      orderCommunicationInput = {
        type: OrderType.Communication,
        state: OrderState.Open,
        creationDate,
        registerInitDate: dataForm.registerInitDate,
        specialTag: dataForm?.specialTag,
        description: dataForm.description,
        classification: dataForm.classification,
        capitol: dataForm.capitol,
        subCapitol: dataForm.subCapitol,
        ...(dataForm.predefine && { predefinedOrder: dataForm.predefine }),
        currentExpeditionIndex: order.indexExpedition,
        parentExpeditionOrderID: order.id,
      };
    }
    /// Step 2: Bloque C-6 — crear OT padre (Generic o Campaign) atómicamente.
    // Antes (Bloque C-1): CREATE_NEW_ORDER + CREATE_NEW_GENERIC_ORDER/CAMPAIGN +
    // UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS post-create (5 mutations).
    // Ahora: 1 sola SAVE_GENERIC_ORDER o SAVE_CAMPAIGN atómica vía $transaction.
    if (tabSelected === 0) {
      const isCampaignParent =
        orderCommunicationInput?.specialTag === OrderType.Campaign;

      if (isCampaignParent) {
        dataOrderParent = await client.mutate({
          mutation: SAVE_CAMPAIGN,
          // El padre de una comunicación no se encola suelto: si se encolara solo
          // él, la SAVE_COMMUNICATION hija se perdería. Sin red del todo ya se entró
          // por la rama compound; aquí, si falla, es error (con toast).
          context: { skipOfflineQueue: true },
          variables: {
            input: {
              // GAP 7 (auditoría flap-safety): idempotencia online — UUID
              // estable por operación (RetryLink reintenta con las mismas
              // variables → el backend no duplica la OT padre si ya se
              // aplicó). Antes solo la comm llevaba clientOpId — un flap con
              // reintento podía duplicar la OT padre (campaña).
              clientOpId: crypto.randomUUID(),
              state: OrderState.Open,
              creationDate,
              registerInitDate: dataForm.registerInitDate,
              pkInitKm: Number(pkInitArray[0]),
              pkInitMeter: Number(pkInitArray[1]),
              pkEndKm: Number(pkEndArray[0]),
              pkEndMeter: Number(pkEndArray[1]),
              track: dataForm.track,
              direction: dataForm?.direction,
              margin: dataForm?.margin,
              lane: dataForm.lane,
              concessionId: dataForm.concession,
              roadId: dataForm.road,
              teamIds: toTeamIds(dataForm.teamRelationships),
              capitolId: orderCommunicationInput?.capitol,
              subCapitolId: orderCommunicationInput?.subCapitol,
              classification: orderCommunicationInput?.classification,
              ...(dataForm.predefine && {
                predefinedOrderId: dataForm.predefine,
              }),
              description: orderCommunicationInput?.description,
              notes: orderCommunicationInput?.notes,
              name: `Campanya relacionada amb una comunicacio`,
            },
          },
          update(cache, { data: { saveCampaign } }) {
            prependCampaignToCache(cache, saveCampaign.campaign);
            if (cursorCampaign?.cursor !== undefined) {
              dispatch(setCursor(tableIds.CAMPAIGNS, {
                ...cursorCampaign, take: takeBase, cursor: '', orderBy: 'desc',
              }));
              dispatch(setCursor(tableIds.CAMPAIGN_MODAL, {
                ...cursorCampaignModal, take: takeBase, cursor: '', orderBy: 'desc',
              }));
            }
          },
        });
      } else {
        dataOrderParent = await client.mutate({
          mutation: SAVE_GENERIC_ORDER,
          // El padre de una comunicación no se encola suelto: si se encolara solo
          // él, la SAVE_COMMUNICATION hija se perdería. Sin red del todo ya se entró
          // por la rama compound; aquí, si falla, es error (con toast).
          context: { skipOfflineQueue: true },
          variables: {
            input: {
              // GAP 7 (auditoría flap-safety): idempotencia online — UUID
              // estable por operación (RetryLink reintenta con las mismas
              // variables → el backend no duplica la OT padre si ya se
              // aplicó). Antes solo la comm llevaba clientOpId — un flap con
              // reintento podía duplicar la OT padre (genérica).
              clientOpId: crypto.randomUUID(),
              state: OrderState.Open,
              creationDate,
              registerInitDate: dataForm.registerInitDate,
              pkInitKm: Number(pkInitArray[0]),
              pkInitMeter: Number(pkInitArray[1]),
              pkEndKm: Number(pkEndArray[0]),
              pkEndMeter: Number(pkEndArray[1]),
              track: dataForm.track,
              direction: dataForm?.direction,
              margin: dataForm?.margin,
              lane: dataForm.lane,
              concessionId: dataForm.concession,
              roadId: dataForm.road,
              teamIds: toTeamIds(dataForm.teamRelationships),
              capitolId: orderCommunicationInput?.capitol,
              subCapitolId: orderCommunicationInput?.subCapitol,
              classification: orderCommunicationInput?.classification,
              ...(dataForm.predefine && {
                predefinedOrderId: dataForm.predefine,
              }),
              description: orderCommunicationInput?.description,
              notes: orderCommunicationInput?.notes,
            },
          },
          update(cache, { data: { saveGenericOrder } }) {
            prependGenericOrderToCache(cache, saveGenericOrder.genericOrder);
            if (cursorGenericOrder?.cursor !== undefined) {
              dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, {
                ...cursorGenericOrder, take: takeBase, cursor: '', orderBy: 'desc',
              }));
              dispatch(setCursor(tableIds.DUPLICATE_GENERIC_ORDER, {
                ...cursorGenericOrderDuplicate, take: takeBase, cursor: '', orderBy: 'desc',
              }));
              dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, {
                ...cursorGenericOrderSearchAndCreate, take: takeBase, cursor: '', orderBy: 'desc',
              }));
              dispatch(setCursor(tableIds.GENERIC_ORDER_MODAL, {
                ...cursorGenericOrderModal, take: takeBase, cursor: '', orderBy: 'desc',
              }));
            }
          },
        });
      }

      // Refetch del Order parent
      const parentOrderId =
        dataOrderParent?.data?.saveCampaign?.order?.id ??
        dataOrderParent?.data?.saveGenericOrder?.order?.id;
      if (parentOrderId) {
        client.query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: parentOrderId },
        });
      }
    }

    /// Step 3: Save communication atómicamente vía SAVE_COMMUNICATION (Bloque C-1).
    //  PLAN-SAVE-ENTITY-UNIFIED.md §2.4 — sustituye CREATE_NEW_ORDER + CREATE_NEW_COMMUNICATION
    //  + UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS por una sola transacción atómica
    //  en backend. Backend infiere indexExpedition/indexType del parent.
    // Bloque C-6: parent ahora viene de SAVE_CAMPAIGN o SAVE_GENERIC_ORDER (atómico).
    const parentOrderIdFromSave =
      dataOrderParent?.data?.saveCampaign?.order?.id ??
      dataOrderParent?.data?.saveGenericOrder?.order?.id;
    const parentOrderIdForSave = parentOrderIdFromSave
      ? parentOrderIdFromSave
      : useParentOrderFromStorage
        ? inheritedParentId
        : storedDataOTSelected?.id;

    const dataSaveCommunication = await client.mutate({
      mutation: SAVE_COMMUNICATION,
      variables: {
        input: {
          // Idempotencia online: UUID estable por operación (RetryLink reintenta
          // con las mismas variables → el backend no duplica si ya se aplicó).
          clientOpId: crypto.randomUUID(),
          parentOrderId: parentOrderIdForSave,
          sendStatus: dataForm.sendStatus,
          state: orderCommunicationInput?.state,
          creationDate,
          registerInitDate: dataForm.registerInitDate,
          pkInitKm: Number(pkInitArray[0]),
          pkInitMeter: Number(pkInitArray[1]),
          pkEndKm: Number(pkEndArray[0]),
          pkEndMeter: Number(pkEndArray[1]),
          track: dataForm.track,
          direction: dataForm?.direction,
          margin: dataForm?.margin,
          lane: dataForm.lane,
          concessionId: dataForm.concession,
          roadId: dataForm.road,
          capitolId: dataForm.capitol,
          subCapitolId: dataForm.subCapitol,
          classification: dataForm.classification,
          ...(dataForm.predefine && { predefinedOrderId: dataForm.predefine }),
          description: dataForm.description,
          notes: dataForm.notes,
          interlocutorGroupId: dataForm.groupInter,
          mediaOfCommunicationId: dataForm.media,
        },
      },

      update(cache, { data: { saveCommunication } }) {
        // Cache prepend + parent childExpeditionOrderIDs push (helper
        // reutilizable; mismo path que ejecuta el sync offline).
        prependCommunicationToCache(
          cache,
          saveCommunication,
          parentOrderIdForSave || undefined,
        );
        // Side effect online: reset cursors para que el usuario vea la nueva
        // comunicación arriba al volver a las listas.
        dispatch(setCursor(tableIds.DUPLICATE_COMMUNICATION, {
          ...cursorDuplicateCommunication, take: takeBase, cursor: '', orderBy: 'desc',
        }));
        dispatch(setCursor(tableIds.WORKORDER_COMMUNICATIONS, {
          ...cursorWorkOrdersCommunication, take: takeBase, cursor: '', orderBy: 'desc',
        }));
        dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, {
          ...cursorSearchAndCreateCommunication, take: takeBase, cursor: '', orderBy: 'desc',
        }));
      },
    });

    // Refetch del Order Communication child
    client.query({
      fetchPolicy: 'network-only',
      query: GET_ORDER_BY_ID,
      variables: { id: dataSaveCommunication.data.saveCommunication.order.id },
    });

  // Clean the form local storage as it was just saved
  // Clean up all possible keys (base, OT, and Campaign) and the owner
  releaseCommunicationCreationDraft();

  dispatch(deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM));
  dispatch(deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM_OT));
  dispatch(deselectedRowModalTable(formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN));

  dispatch(indexTabFormReset(tabsType.COMMUNICATION));

    // ========== Context-aware redirect logic ==========
    // Check if we came from the Communications standalone section
    const navigationContextRaw = sessionStorage.getItem(
      formNaming.COMMUNICATION_NAVIGATION_CONTEXT
    );
    let navigationContext = null;
    if (navigationContextRaw) {
      try {
        navigationContext = JSON.parse(
          decompressSessionStorage(navigationContextRaw)
        );
      } catch {
        navigationContext = null;
      }
    }
    const navigationOrigin = navigationContext?.origin;

    // Clean up navigation context after reading
    sessionStorage.removeItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);

    // Bloque C-6: la sync UPDATE_ORDER+UPDATE_ORDER_RELATIONSHIPS para el parent
    // ya no es necesaria — SAVE_GENERIC_ORDER/SAVE_CAMPAIGN crean Order+Entity
    // atómicamente con todos los campos Tipo B (concession, road, PK, teams)
    // coherentes desde el primer momento. Igualmente la sync para la Order
    // Communication ya quedó eliminada en Bloque C-1.

    // Redirect based on tab selection - handles all origins correctly
    // (BUG #24 fix: removed origin check that incorrectly redirected to /communication)
    if (tabSelected === 0 && parentOrderIdFromSave) {
      // Tab 0: Redirect to the newly created parent order
      const isCampaign = dataForm.specialTag === OrderType.Campaign;
      return redirect(isCampaign ? `/work-orders/campaign/${parentOrderIdFromSave}` : `/work-orders/${parentOrderIdFromSave}`);
    } else if (tabSelected === 1) {
      // Tab 1: Redirect to related OT (either from ShowOT parent or modal selection)
      const orderId = useParentOrderFromStorage ? inheritedParentId : storedDataOTSelected?.id;
      // Detect if parent is a campaign based on ROID starting with "Ca"
      const parentRoidRaw = useParentOrderFromStorage ? dataForm.parentOrderRoid : storedDataOTSelected?.roid;
      const parentRoid = Array.isArray(parentRoidRaw) ? parentRoidRaw.join('.') : parentRoidRaw;
      const isCampaign = parentRoid?.startsWith('Ca');
      return redirect(isCampaign ? `/work-orders/campaign/${orderId}` : `/work-orders/${orderId}`);
    } else if (tabSelected === 2) {
      // Tab 2: Redirect to related Campaign
      const campaignId = useParentOrderFromStorage ? inheritedParentId : storedDataOTSelected?.id;
      return redirect(`/work-orders/campaign/${campaignId}`);
    }

    // Fallback: should not reach here, but redirect to previous page if it does
    return redirect('..');
  }

  return redirect('..');
};
